import { Buffer } from 'buffer';
import { useSnackbar } from 'notistack';
import { deflate, inflate } from 'pako';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import { apiBridges } from 'constants/api';
import { WebL3Fields } from 'constants/inspectionFields';
import { InspectionMainTab } from 'constants/inspections';
import usePrevious from 'hooks/usePrevious';
import { useGlobalSelector, useInspectionSelector } from 'stores';
import { mappingDataInL3 } from '../utils/mappingInL3';
import { mappingDataOutL3 } from '../utils/mappingOutL3';

// Issue #1021, punto 24 — percentuale di completamento web dell'attività L3,
// sempre "-" in lista prima di questo fix: sotto, `completionPercentage`
// veniva solo letto dalla GET e rimandato invariato alla PUT (mai calcolato
// per L3). Funzione standalone, non l'hook useCompletionInspection() (usato
// da L0/L1 in CreateInspectionButtons.jsx): quell'hook legge da
// useFormContext(), che qui risolverebbe al form condiviso L0/L1 (sbagliato,
// L3 ha un proprio useForm() separato, vedi commento sotto), non a
// submittedData. I campi L3 sono tutti piatti (niente isTable/subfields, a
// differenza di WebL0Fields/WebL1Fields), non serve la logica completa
// dell'hook.
const computeL3CompletionPercentage = values => {
  const getValues = fieldName => values?.[fieldName];
  let counter = 0;
  let total = 0;

  Object.values(WebL3Fields.Idraulica).forEach(fieldArray => {
    fieldArray.forEach(({ fieldName, condition }) => {
      // eslint-disable-next-line no-eval
      if (condition && !eval(condition)) return;
      total++;
      const value = getValues(fieldName);
      const isFilled =
        value !== undefined &&
        value !== null &&
        value !== '' &&
        (!Array.isArray(value) || value.length > 0);
      if (isFilled) counter++;
    });
  });

  return total > 0 ? Math.floor((counter / total) * 100) : 0;
};

// Mirror leggero e indipendente di ../useInspection.js, dedicato solo alla
// tab L3 (piano PIANO-IMPLEMENTAZIONE-L3.md §0.4/§0.7/§4.3): un proprio
// useForm(), senza nessuna delle side-effect L0/L1-specifiche
// (ricalcolo prossima data ispezione, mappingInAll/mappingOutCommon).
const useInspectionL3 = ({ inspectionId }) => {
  const { put, get } = useFetch();
  const { enqueueSnackbar } = useSnackbar();
  const { showLoader, hideLoader, activeTabLevel } = useGlobalSelector();
  const prevActive = usePrevious(activeTabLevel);
  const { version, setVersion } = useInspectionSelector();

  // Echo-back di status: il payload di InspectionDiagnosisUpdatesDto lo
  // richiede sempre (vedi apps/api/.../bridges.service.ts updateDiagnosis,
  // che fa `{ ...dbInspection, ...updatedInspection }`) — per non alterarlo
  // quando si salva solo L3, lo leggiamo alla GET e lo rimandiamo invariato
  // alla PUT. completionPercentage (web) non è tra questi — vedi
  // computeL3CompletionPercentage in testa al file, issue #1021 punto 24.
  // completionPercentageApp NON è più un echo-back (issue #1021,
  // segnalazione cliente 2026-09-25): un'attività L3 non ha un proprio
  // flusso di compilazione da app (è una valutazione preliminare idraulica
  // compilabile solo da web), quindi quel campo non veniva mai calcolato da
  // nessuno — restava sempre il valore letto la prima volta (spesso 0),
  // mostrando "0%" in app anche per un'attività compilata al 100% da web.
  // Ora rispecchia semplicemente lo stesso valore calcolato per il web
  // (sotto, in onSubmit) — nessuno stato/echo-back separato da mantenere.
  const [inspectionStatus, setInspectionStatus] = useState();

  const methods = useForm({ defaultValues: {} });
  const { handleSubmit, reset, formState } = methods;

  const handleDetail = useCallback(async () => {
    if (
      prevActive !== activeTabLevel &&
      activeTabLevel === InspectionMainTab.L3_IDRAULICA
    ) {
      try {
        showLoader();
        const result = await get(`${apiBridges.INSPECTIONS}/${inspectionId}`);
        // use-http annulla la richiesta precedente se ne parte una nuova
        // sulla stessa istanza (può succedere qui: activeTabLevel si
        // aggiorna nello store globale subito dopo il mount, e questo effect
        // può rieseguire prima che la prima GET sia completata) — una
        // richiesta annullata risolve a `undefined` senza passare dagli
        // interceptor di HttpProvider (quelli lanciano solo su risposta
        // HTTP non-ok), quindi non è un errore reale: la rieseguzione più
        // recente dell'effect completerà correttamente il reset.
        if (!result) return;

        // unzip (inflate) diagnosis - stesso pattern di useInspection.js
        const inflatedDiagnosis = inflate(
          Buffer.from(result.diagnosis, 'base64'),
          { to: 'string' }
        );
        const diagnosis = JSON.parse(inflatedDiagnosis);

        setVersion(result?.version);
        setInspectionStatus(result?.status);

        reset(mappingDataInL3(diagnosis));
      } catch (err) {
        console.error('Error in read diagnosis (L3): ', err);
        enqueueSnackbar(
          `Si è verificato un errore nella lettura dell'ispezione`,
          { variant: 'error' }
        );
      } finally {
        hideLoader();
      }
    }
  }, [activeTabLevel, inspectionId, prevActive]);

  useEffect(() => {
    inspectionId && handleDetail().catch(console.error);
  }, [inspectionId, activeTabLevel]);

  const onSubmit = useCallback(
    async submittedData => {
      // Issue #1021, punto 24 — completionPercentage era finora un puro
      // echo-back (letto alla GET, rimandato invariato), mai calcolato per
      // L3: da qui in poi riflette submittedData appena inviato.
      // completionPercentageApp — segnalazione cliente 2026-09-25 — rispecchia
      // lo stesso valore: un'attività L3 non ha un proprio flusso di
      // compilazione da app (vedi commento su completionPercentageApp
      // in testa al file), quindi non c'è nessun valore "app" alternativo
      // da preservare.
      const completionPercentage = computeL3CompletionPercentage(submittedData);
      const body = {
        diagnosis: mappingDataOutL3(submittedData),
        status: inspectionStatus,
        completionPercentage,
        completionPercentageApp: completionPercentage,
      };

      try {
        showLoader();
        // zip (deflate) diagnosis - stesso pattern di useInspection.js
        const compressedBody = deflate(JSON.stringify(body));
        const compressedBodyBuffer = Buffer.from(compressedBody);
        const compressedBodyBufferString =
          compressedBodyBuffer.toString('base64');

        await put(`${apiBridges.INSPECTIONS}/${inspectionId}/diagnosis`, {
          zip: compressedBodyBufferString,
          version,
        });

        // e-2026-08-18 — la PUT sopra risponde con InspectionBaseDto (niente
        // diagnosis, vedi inspection-base.dto.ts): il salvataggio ricalcola
        // e persiste comunque Level3.cdaGraphicalRepresentation lato BE
        // (InspectionsService.calculateHydraulicCdaL3, chiamata dentro
        // updateInspectionDiagnosis), ma senza questa rilettura il form
        // resterebbe con il valore precedente finché non si esce e si
        // rientra nella tab L3 (che rifà la GET in handleDetail). Stessa GET
        // usata lì, riusiamo lo stesso mappingDataInL3 per il reset sotto.
        const result = await get(`${apiBridges.INSPECTIONS}/${inspectionId}`);

        if (result) {
          const inflatedDiagnosis = inflate(
            Buffer.from(result.diagnosis, 'base64'),
            { to: 'string' }
          );
          const diagnosis = JSON.parse(inflatedDiagnosis);

          // Stesso echo-back di handleDetail — senza questo, un secondo
          // salvataggio consecutivo (senza uscire dalla tab) ripartirebbe
          // con una `version` non aggiornata.
          setVersion(result?.version);
          setInspectionStatus(result?.status);

          // reset() (non un setValue mirato) è voluto: un salvataggio
          // riuscito deve azzerare formState.isDirty su TUTTI i campi della
          // tab L3 — è il segnale che pilota l'avviso "dati non salvati" in
          // Accordion5CdaL3.jsx. mappingDataInL3(diagnosis), non
          // submittedData grezzo: react-hook-form ricalcola isDirty subito
          // dopo un reset() confrontando i valori correnti col nuovo
          // baseline, e submittedData contiene `undefined` sui campi mai
          // toccati — la ri-registrazione degli input dopo reset()
          // materializza questi campi con il default del wrapper ('', [],
          // ecc.), che non è più deep-equal a `undefined`, rimettendo
          // isDirty a true. mappingDataInL3 (la stessa funzione già usata da
          // handleDetail sopra) normalizza sempre a null/numeri veri,
          // eliminando la divergenza.
          reset(mappingDataInL3(diagnosis));
        } else {
          // GET annullata (use-http, richiesta concorrente sulla stessa
          // istanza) — fallback conservativo al comportamento precedente.
          reset(submittedData);
        }

        return enqueueSnackbar(`Salvataggio effettuato`, {
          variant: 'success',
        });
      } catch (err) {
        console.error('Error in save diagnosis (L3): ', err);
        return enqueueSnackbar(
          `Si è verificato un errore nel salvataggio dell'ispezione`,
          { variant: 'error' }
        );
      } finally {
        hideLoader();
      }
    },
    [inspectionId, version, inspectionStatus]
  );

  return {
    methods,
    handleSubmit,
    onSubmit,
    formState,
  };
};

export default useInspectionL3;
